import moment from "moment";
import * as configUtils from "util/config";
import { validateCUIT } from "util/form";
import * as i18n from "util/i18n";
import * as Yup from "yup";

export const creditBillOperations = configUtils.getArray("creditBill.actions", [
    "TRANSMISSION",
    "TRANSMISSION-ADMIT",
    "TRANSMISSION-REPUDIATE",
    "TRANSMISSION-ANULATE",
    "RETURN",
    "RETURN-ACCEPT",
    "RETURN-ANULATE",
    "RETURN-DECLINE",
    "INFORM-CBU",
]);

export const TRANSMISSION = creditBillOperations[0];
export const TRANSMISSION_ADMIT = creditBillOperations[1];
export const TRANSMISSION_REPUDIATE = creditBillOperations[2];
export const TRANSMISSION_ANULATE = creditBillOperations[3];
export const RETURN = creditBillOperations[4];
export const RETURN_ACCEPT = creditBillOperations[5];
export const RETURN_ANULATE = creditBillOperations[6];
export const RETURN_DECLINE = creditBillOperations[7];
export const INFORM_CBU = creditBillOperations[8];

export const getBoolAction = (action = "") => {
    const _action = action.toUpperCase();

    const isTransmission = _action === TRANSMISSION;
    const isTransmissionAdmit = _action === TRANSMISSION_ADMIT;
    const isTransmissionRepudiate = _action === TRANSMISSION_REPUDIATE;
    const isTransmissionAnulate = _action === TRANSMISSION_ANULATE;
    const isReturn = _action === RETURN;
    const isReturnAccept = _action === RETURN_ACCEPT;
    const isReturnAnulate = _action === RETURN_ANULATE;
    const isReturnDecline = _action === RETURN_DECLINE;
    const isInformCbu = _action === INFORM_CBU;

    return {
        isTransmission,
        isTransmissionAdmit,
        isTransmissionRepudiate,
        isTransmissionAnulate,
        isReturn,
        isReturnAccept,
        isReturnAnulate,
        isReturnDecline,
        isInformCbu,
    };
};

export const creditBillStates = configUtils.getArray("creditBill.states", [
    "TODOS",
    "ACTIVA",
    "ACTIVA-PENDIENTE",
    "DEVOLUCION-PENDIENTE",
    "NEGOCIACION-PENDIENTE",
    "NEGOCIACION",
    "PAGA",
    "IMPAGA",
]);

export const ACTIVA = creditBillStates[1];
export const ACTIVA_PENDIENTE = creditBillStates[2];
export const DEVOLUCION_PENDIENTE = creditBillStates[3];
export const NEGOCIACION_PENDIENTE = creditBillStates[4];
export const NEGOCIACION = creditBillStates[5];
export const PAGA = creditBillStates[6];
export const IMPAGA = creditBillStates[7];

export const getBoolState = (state) => {
    const isActive = state === ACTIVA;
    const isActivePending = state === ACTIVA_PENDIENTE;
    const isRetunPending = state === DEVOLUCION_PENDIENTE;
    const isNegotiationPending = state === NEGOCIACION_PENDIENTE;
    const isNegotiation = state === NEGOCIACION;
    const isPaid = state === PAGA;
    const isUnPaid = state === IMPAGA;

    return { isActive, isActivePending, isNegotiation, isNegotiationPending, isRetunPending, isPaid, isUnPaid };
};

export const statusTagColor = (state) => {
    switch (state) {
        case ACTIVA:
            return "success";
        case ACTIVA_PENDIENTE:
            return "warning";
        case DEVOLUCION_PENDIENTE:
            return "warning";
        case PAGA:
            return "warning";
        case IMPAGA:
            return "warning";
        case "COR000I":
            return "success";
        case "COR023I":
            return "warning";
        default:
            return "error";
    }
};

export const billTabs = {
    EMITTED: "EMITTED",
    IN_TENURE: "IN_TENURE",
    RECEIVED: "RECEIVED",
    TO_PAY: "TO_PAY",
};

export const billTabsLower = {
    EMITTED: "emitted",
    IN_TENURE: "in_tenure",
    RECEIVED: "received",
    TO_PAY: "to_pay",
};

export const getBoolOption = (tabOption) => {
    const isInTenure = tabOption === billTabs.IN_TENURE;
    const isToPay = tabOption === billTabs.TO_PAY;
    const isReceived = tabOption === billTabs.RECEIVED;
    const isEmitted = tabOption === billTabs.EMITTED;
    return { isEmitted, isInTenure, isToPay, isReceived };
};

export const initialFilters = {
    pageNumber: 1,
    rowsPerPage: 20,
    orderBy: "facturas.fecha_emision",
    issueDateFrom: null,
    issueDateTo: null,
    paymentDueDateFrom: null,
    paymentDueDateTo: null,
    invoiceStatus: "TODOS",
};

export const emittedFilters = {
    buyerCuit: null,
    holderCuit: null,
};

export const inTenureFilters = {
    buyerCuit: null,
    issuerCuit: null,
};

export const recievedFilters = {
    holderCuit: null,
    issuerCuit: null,
};

export const toPayFilters = {
    holderCuit: null,
    issuerCuit: null,
};

export const filterByTabList = [emittedFilters, inTenureFilters, recievedFilters, toPayFilters];

export const customValuesByTab = {
    [billTabs.EMITTED]: emittedFilters,
    [billTabs.IN_TENURE]: inTenureFilters,
    [billTabs.RECEIVED]: recievedFilters,
    [billTabs.TO_PAY]: toPayFilters,
};

export const cuitValidations = (cuit1, cuit2) => () =>
    Yup.object().shape({
        [cuit1]: Yup.string()
            .nullable()
            .test("format-cuit", i18n.get("onboarding.step.verifyCUIT.cuit.error.invalidFormat"), (value) =>
                validateCUIT(value),
            ),
        [cuit2]: Yup.string()
            .nullable()
            .test("format-cuit", i18n.get("onboarding.step.verifyCUIT.cuit.error.invalidFormat"), (value) =>
                validateCUIT(value),
            ),
    });

export const FORM_ID = "creditBill";

export const transmissionTypes = configUtils.getArray("creditBill.transmit.types", ["NOM", "NEG"]);

export const beneficiaryTypes = configUtils.getArray("creditBill.beneficiary.types", ["PNFC", "BCRA"]);

export const formatAction = (action = "") => {
    const actionInLowerCase = action.toLowerCase();
    const actionI18n = actionInLowerCase?.replace("-", ".");
    return { actionI18n, actionInLowerCase };
};

export const validateAction = (bill) => {
    const { isActive, isActivePending, isNegotiationPending, isRetunPending, isUnPaid } = getBoolState(
        bill?.estadoFactura,
    );
    const today = moment();
    const isDueDate = today.diff(moment(bill?.fechaVencimientoPago), "days") <= 0;
    const isDueDate3workDaysBefore = moment(bill?.fechaVencimientoPago).diff(today, "days") >= 3;

    const transmission = {
        canTransmit: isActive && isDueDate3workDaysBefore,
        canAnulate: isActivePending || isNegotiationPending,
        canAdmit: (isActivePending || isNegotiationPending) && isDueDate3workDaysBefore,
        canRepudiate: isActivePending || isNegotiationPending,
    };

    const returnFcem = {
        canReturn: (isActive && isDueDate) || (isUnPaid && !bill.calEmitido),
        canAccept: isRetunPending || isUnPaid,
        canDecline: (isRetunPending || isUnPaid) && isDueDate,
        canAnulate: isRetunPending || isUnPaid,
    };

    const canInformFromTenure = (isActive || isActivePending) && isDueDate;
    const canInformFromPay = (isActive || isActivePending || isNegotiationPending || isRetunPending) && isDueDate;

    return {
        transmission,
        returnFcem,
        canInformFromTenure,
        canInformFromPay,
    };
};

export const monedas = {
    PES: "ARS",
    DOL: "USD",
};
